import { useState, useMemo, forwardRef, ChangeEvent } from 'react';

import { toUpper } from 'lodash';
import { RiErrorWarningLine } from 'react-icons/ri';

import {
  TextInputProps as MantineTextInputProps,
  TextInput as MantineTextInput
} from '@mantine/core';

import { InputLoader } from '../../feedback';
import { useMergeClassNames } from '../../utils';

import { InputOverrideClassNames } from './TextInput.styles';

interface TextInputProps extends MantineTextInputProps {
  loading?: boolean;
  enableRightSectionEvents?: boolean;
  capitalize?: boolean;
  trimSpaces?: boolean;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const {
    size = 'md',
    leftSection,
    rightSection,
    loading,
    error,
    enableRightSectionEvents,
    capitalize: uppercase,
    trimSpaces = true,
    classNames,
    onChange,
    onFocus,
    onBlur,
    ...rest
  } = props;
  const [focussed, setFocussed] = useState<boolean>(false);

  const rightSectionFinal = loading ? (
    <InputLoader size={size} />
  ) : error ? (
    <RiErrorWarningLine />
  ) : rightSection ? (
    rightSection
  ) : undefined;

  const classes = useMemo(() => {
    return InputOverrideClassNames({
      size,
      error,
      enableRightSectionEvents,
      rightSection: rightSectionFinal,
      focussed
    });
  }, [size, focussed, error, enableRightSectionEvents, rightSectionFinal]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocussed(true);
    onFocus && onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocussed(false);
    e.target.value = e.target.value?.trim ? e.target.value?.trim() : e.target.value;
    onBlur && onBlur(e);
  };

  const handleCasing = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.value = toUpper(event.target.value);
    onChange && onChange(event);
  };

  const handleSpaceTrimming = (event: ChangeEvent<HTMLInputElement>) => {
    // Remove leading spaces and replace multiple consecutive spaces with a single space
    event.target.value = event.target.value.replace(/^\s+/, '').replace(/\s+/g, ' ');
    onChange && onChange(event);
  };

  const handleCasingAndSpaceTrimming = (event: ChangeEvent<HTMLInputElement>) => {
    // Apply both transformations
    // Remove leading spaces and replace multiple consecutive spaces with a single space
    event.target.value = event.target.value.replace(/^\s+/, '').replace(/\s+/g, ' ');
    // Convert to uppercase
    event.target.value = toUpper(event.target.value);
    onChange && onChange(event);
  };

  const getChangeHandler = () => {
    if (uppercase && trimSpaces) {
      return handleCasingAndSpaceTrimming;
    } else if (uppercase) {
      return handleCasing;
    } else if (trimSpaces) {
      return handleSpaceTrimming;
    }
    return onChange;
  };

  const mergedClasses = useMergeClassNames({
    classNames: [classes, classNames]
  });

  return (
    <MantineTextInput
      {...rest}
      ref={ref}
      onFocus={handleFocus}
      onBlur={handleBlur}
      size={size}
      leftSection={leftSection}
      rightSection={rightSectionFinal}
      error={error}
      classNames={mergedClasses}
      onChange={getChangeHandler()}
    />
  );
});

export { TextInput };
export type { TextInputProps };
